import type { AlbumSummary, Artist, Track } from '@blackblack/shared';
import { api } from '../../lib/api';
import { useQuery } from '../../lib/query';
import { Link, useParams } from '../../lib/router';
import { player } from '../../state/player';
import { Button, ErrorState, Skeleton } from '../../components/ui/primitives';
import { Page, PageHeader, Section, Shelf } from '../../components/layout';
import { Cover } from './Cover';
import { TrackRow } from './TrackRow';
import { RowSkeleton } from './MusicPage';
import styles from './music.module.css';

type ArtistPayload = Artist & { tracks: Track[]; albums: AlbumSummary[] };

export function ArtistPage() {
  const { id } = useParams<{ id: string }>();
  const q = useQuery<ArtistPayload>(`artist:${id}`, () => api.get(`/artists/${id}`));
  const a = q.data;

  return (
    <Page>
      <PageHeader title={a?.name} fallback="/music" />
      {q.error && <ErrorState error={q.error} onRetry={q.refetch} />}
      <div className={styles.artistHero}>
        {a ? <Cover src={a.imageUrl} size={140} radius={70} /> : <Skeleton w={140} h={140} r={70} />}
        <h1>{a?.name ?? <Skeleton w={160} h={26} />}</h1>
        {a?.bio && <p>{a.bio}</p>}
        {a && (
          <Button icon="play" disabled={!a.tracks.some((t) => t.streamUrl)} onClick={() => player.playTracks(a.tracks, 0, a.name)}>
            Afspelen
          </Button>
        )}
      </div>
      <Section title="Populair">
        <div className={styles.rows}>
          {!a ? [0, 1, 2].map((i) => <RowSkeleton key={i} />) : a.tracks.map((t, i) => <TrackRow key={t.id} track={t} list={a.tracks} index={i} context={a.name} number={i + 1} />)}
        </div>
      </Section>
      {a && a.albums.length > 0 && (
        <Section title="Albums">
          <Shelf>
            {a.albums.map((al) => (
              <Link key={al.id} to={`/album/${al.id}`} className={styles.card}>
                <span className={styles.cardArt}>
                  <Cover src={al.coverUrl} size="100%" radius={14} />
                </span>
                <span className={styles.cardTitle}>{al.title}</span>
                <span className={styles.cardSub}>{al.releaseDate?.slice(0, 4)} · Album</span>
              </Link>
            ))}
          </Shelf>
        </Section>
      )}
    </Page>
  );
}
